import { captchaEnabled, lang, regulatedCompanyNavigation, isCn, hubSpot } from 'config';

import React, {useEffect, useRef, useState} from 'react';

import {withRouter} from 'react-router-dom';
import {NotAvailableInYourCountryModal} from "src/components/auth/modals/NotAvailableInYourCountryModal";
import {useModalService} from "src/services/ModalService";
import {withCopies} from 'src/services/TranslationService';
import {withProfile} from 'src/services/ProfileService';
import {withCaptcha} from 'src/hoc/captcha';

// Utils
import {validationsForm, checkStatusField} from 'src/utils/validations-form';

// Api
import {register, signLegals} from 'src/api/authentication';

import {AuthLayout} from "../components/auth/AuthLayout";
import {SignUpInfo} from "../components/auth/SignUpInfo";
import {SignUpForm} from "../components/auth/sign-up/SignUpForm";
import {
    getAffiliateName,
    getCountryByIp,
    getForbiddenCountries,
    getMifidCountries,
    getRegulatedCompaniesCountries
} from "../api/commons";
import {getCookie, hasCookie} from "../utils/cookies";
import {regulatedCompaniesEnum} from "../enums/regulated-companies";
import { WarningDxSyc } from "../components/auth/modals/warning-dx-syc/WarningDxSyc";
import { WarningDxFca } from "../components/auth/modals/warning-dx-syc/WarningDxFca";
import {getRegisterType, hasCNMV, isEUGroupSelected, setCountryAndRegulatedCompany} from "../utils/auth";
import {dxRegisterTypeEnum} from "../enums/dx-register-type";
import {
    CompareRegulatedCompanies
} from "../components/auth/modals/compare-regulated-companies/CompareRegulatedCompanies";

const WELCOME_PAGE = '/welcome';

const RegisterPage = props => {
    const { modals, showModal, hideModal} = useModalService();

    const [errorsRequest, setErrorsRequest] = useState(null);
    const [fieldsForm, setFieldsForm] = useState({
        username: '',
        email: '',
        password: '',
        commercialCommunicationsAccepted: false,
    });
    const [timeoutForm, setTimeoutForm] = useState(null);
    const [fieldsStatus, setFieldsStatus] = useState({});
    const [fieldsStatusType, setFieldsStatusType] = useState({});
    const [formStatus, setFormStatus] = useState(false);
    const [countryByIp, setCountryByIp] = useState(null);
    const [registerCountries, setRegisterCountries] = useState([]);
    const [regulatedCompany, setRegulatedCompany] = useState(null);
    const [regulatedCompanies, setRegulatedCompanies] = useState([]);
    const [registerCountry, setRegisterCountry] = useState('');
    const [affiliateName, setAffiliateName] = useState(null);
    const [referredActive, setReferredActive] = useState(false);
    const [EUCountries, setEUCountries] = useState([]);

    const fieldsFormRef = useRef(fieldsForm);
    const regulatedCompanyRef = useRef(regulatedCompany);
    const registerCountryRef = useRef(registerCountry);
    const referredActiveRef = useRef(referredActive);

    const usernameInput = useRef();
    const emailInput = useRef();
    const passwordInput = useRef();
    const commercialCommunicationsAcceptedInput = useRef();

    let captchaScript = null;

    useEffect(() => {
        fieldsFormRef.current = fieldsForm;
    }, [fieldsForm]);

    const showModalWarning = () => {
        if (EUCountries.includes(registerCountry) && regulatedCompany === regulatedCompaniesEnum.DX_FCA) {
            showModal('warningDxFca');
        } else if (isEUGroupSelected(regulatedCompanies, regulatedCompany)) {
            showModal('warningDxSyc');
        }
    }

    useEffect(() => {
        regulatedCompanyRef.current = regulatedCompany;
        showModalWarning();
    }, [regulatedCompany]);

    useEffect(() => {
        registerCountryRef.current = registerCountry;
        showModalWarning();
    }, [registerCountry]);

    useEffect(() => {
        referredActiveRef.current = referredActive;
    }, [referredActive]);

    const getAffiliate = () => {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const ac = urlParams.get('ac') ? encodeURIComponent(urlParams.get('ac')) : hasCookie('dx.inv.code') ? encodeURIComponent(getCookie('dx.inv.code')) : null;
        if (ac !== null) {
            getAffiliateName(ac).then((response) => {
                setAffiliateName(response.affiliate_name);
            });
        }
    };

    useEffect(() => {
        if (captchaEnabled === 'true') {
            captchaScript = document.createElement('script');
            captchaScript.id = 'captchaRegisterJs';
            captchaScript.src = `https://www.google.com/recaptcha/api.js?hl=${lang}`;
            captchaScript.async = true;
            captchaScript.onload = () => {
                const {isEnabledCaptcha} = props;
                isEnabledCaptcha('recaptcha-register', (token) => doRegister({
                    token,
                    fieldsFormValues: fieldsFormRef.current,
                    regulatedCompanyValue: regulatedCompanyRef.current,
                    registerCountryValue: registerCountryRef.current,
                    referredActiveValue: referredActiveRef.current,
                }));
            };
            document.body.appendChild(captchaScript);
        }

        getMifidCountries().then((allCountries) => {
            getForbiddenCountries().then((forbiddenCountries) => {
                getRegulatedCompaniesCountries().then((regulatedCompaniesCountries) => {
                    const countries = [...allCountries];
                    countries.forEach((c) => {
                        const country = regulatedCompaniesCountries.find(co => co.name === c.name);
                        const isForbiddenCountry = forbiddenCountries.find(fc => fc === c.id);
                        const regulatedCompaniesZero = [regulatedCompaniesEnum.DX_ZERO];
                        let regulatedCompaniesFormatted = [];
                        const allowOnlyCNMVRegCo = getRegisterType() === dxRegisterTypeEnum.ADV_EU;
                        if (country) {
                            regulatedCompaniesFormatted = allowOnlyCNMVRegCo ? country.regulatedCompanies.filter(item => item === regulatedCompaniesEnum.DX_CNMV) : country.regulatedCompanies;
                        }
                        c.regulatedCompanies = country ? (regulatedCompaniesFormatted.length > 0 ? regulatedCompaniesFormatted : regulatedCompaniesZero) : (isForbiddenCountry ? [] : regulatedCompaniesZero);
                    });
                    setRegisterCountries(countries.length > 0 ? countries.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0)) : countries);
                });
            });
        });

        getAffiliate();

        return () => {
            if (captchaEnabled === 'true' && captchaScript) {
                document.body.removeChild(captchaScript);
            }
        }
    }, []);

    useEffect(() => {
        if (registerCountries.length > 0) {
            const EUGroup = [];
            registerCountries.forEach(rc => {
                if (hasCNMV(rc.regulatedCompanies)) {
                    EUGroup.push(rc.id);
                }
            })
            setEUCountries(EUGroup);
            getCountryByIp().then((cByIp) => {
                if (cByIp.id) {
                    setCountryByIp(cByIp.id);
                } else {
                    updateCountryAndRegulatedCompany();
                }
            }).catch(() => {
                updateCountryAndRegulatedCompany();
            });
        }
    }, [registerCountries]);

    useEffect(() => {
        if (registerCountries.length > 0) {
            updateCountryAndRegulatedCompany();
        }
    }, [countryByIp]);

    const updateCountryAndRegulatedCompany = (event) => {

        const result = setCountryAndRegulatedCompany({
            event,
            regulatedCompanyNavigation,
            regulatedCompany,
            registerCountries,
            countryByIp,
            lang
        });

        if (result.newRegulatedCompany === regulatedCompaniesEnum.DX_ZERO) {
            showModal('darwinexNotAvailableInYourCountry');
        }

        setRegulatedCompany(result.newRegulatedCompany);
        setRegulatedCompanies(result.regulatedCompanies);
        setRegisterCountry(result.registerCountry);
    }

    const sendAnalytics = (data) => {
        try {
            if (typeof dataLayer !== 'undefined') {
                dataLayer.push({
                    "event": "Sign Up - Completed",
                    "customCategory": "Sign Up",
                    "customAction": "COMPLETED"
                });
            }
            if (typeof _hsq !== 'undefined') {
                _hsq.push(["identify", {
                    email: data.email,
                    username: data.username
                }]);
            }
            if (typeof __adroll !== 'undefined') {
                __adroll.record_user({"adroll_segments": "c24729b4"})
            }
        } catch (ex) {}

        /* HUBSPOT */
        const hubspotApiUrl = hubSpot.hubspotApiUrl;
        const fields = [
            {
                name: 'email',
                value: data.email,
            }, {
                name: 'username',
                value: data.username,
            },
        ];

        const communications = hubSpot.communications;
        let legalConsentOptions = {
            consent: {
                consentToProcess: true,
                text: 'Legitimate interest.',
                communications: [],
            },
        };
        for (let i=0; i<communications.length; i++) {
            legalConsentOptions['consent']['communications'].push({
                value: true,
                subscriptionTypeId: communications[i],
                text: 'Legitimate interest.',
            });
        }

        const form = {
            fields,
            legalConsentOptions,
            context : {},
        };

        const request = new XMLHttpRequest();
        request.open('POST', hubspotApiUrl, false);
        request.setRequestHeader('Content-type', 'application/json');
        request.send(JSON.stringify(form));

        /* REDIRECT WELCOME PAGE */
        location.href = WELCOME_PAGE;
    }

    const doRegister = (params) => {
        const {
            token,
            fieldsFormValues,
            regulatedCompanyValue,
            registerCountryValue,
            referredActiveValue,
        } = params;

        setFormStatus(null);

        const { currentLanguage } = props;

        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);

        let source = null;
        if (urlParams.get('utm_source')) {
            source = urlParams.get('utm_source');
        } else if (urlParams.get('itm_source')) {
            source = urlParams.get('itm_source');
        }
        source = encodeURIComponent(source);

        let flowOptions = {};
        if (urlParams.get('set')) {
            flowOptions = {
                set: encodeURIComponent(urlParams.get('set')),
            }
        }

        const data = {
            username: fieldsFormValues.username,
            email: fieldsFormValues.email,
            password: fieldsFormValues.password,
            captchaResponse: typeof token === 'undefined' ? null : token,
            flow: 'register',
            flowOptions,
            source,
            referredActive: !referredActiveValue,
            regulatedCompany: regulatedCompanyValue,
            registerCountry: registerCountryValue,
        };

        register(data, {
            language: currentLanguage
        }).then(() => {
            if (!isCn) {
                sendAnalytics({
                    username: fieldsFormValues.username,
                    email: fieldsFormValues.email,
                });
            } else {
                location.href = WELCOME_PAGE;
            }
        }).catch(() => {
            setErrorsRequest('error-request');
            setFormStatus(true);
        });
    }

    const usernameRule = {
        ref: usernameInput,
        validations: [{
            type: 'required',
            error: 'new-login.signup-form.username.error.required'
        }, {
            type: 'length',
            options: {
                min: 4,
                max: 18,
            },
            error: 'new-login.signup-form.username.error.length'
        }, {
            type: 'alphanumeric',
            error: 'new-login.signup-form.username.error.alphanumeric'
        }, {
            type: 'checkUsername',
            error: 'new-login.signup-form.username.error.checkUsername'
        }]
    };

    const emailRule = {
        ref: emailInput,
        validations: [{
            type: 'required',
            error: 'new-login.signup-form.email.error.required'
        }, {
            type: 'email',
            error: 'new-login.signup-form.email.error.email'
        }, {
            type: 'checkEmail',
            error: 'new-login.signup-form.email.error.checkEmail'
        }]
    };

    const passwordRule = {
        ref: passwordInput,
        validations: [{
            type: 'required',
            error: 'new-login.signup-form.password.error.required'
        }, {
            type: 'length',
            options: {
                min: 8,
                max: 20,
            },
            error: 'new-login.signup-form.password.error.length'
        }, {
            type: 'passwordRegister',
            options: {
                value2: usernameInput,
            },
            error: 'new-login.signup-form.password.error.required',
        }]
    };

    const commercialCommunicationsAcceptedRule = {
        ref: commercialCommunicationsAcceptedInput,
        validations: [{
            type: 'requiredCheck',
            error: 'new-login.allow-for-commercial-communications.error'
        }]
    };

    const handleValidation = (ref = null) => {
        let rules = [];
        if (!ref) {
            rules = [usernameRule, emailRule, passwordRule, commercialCommunicationsAcceptedRule];
        } else {
            if (ref === usernameInput) {
                rules = [usernameRule];
            } else if (ref === emailInput) {
                rules = [emailRule];
            } else if (ref === passwordInput) {
                rules = [passwordRule];
            } else if (ref === commercialCommunicationsAcceptedInput) {
                rules = [commercialCommunicationsAcceptedRule];
            }
        }

        const validationForm = validationsForm(rules);

        setFieldsStatus({ ...fieldsStatus, ...validationForm.fieldsStatus});
        setFieldsStatusType({ ...fieldsStatusType, ...validationForm.fieldsStatusType});
        setFormStatus(!ref ? validationForm.validated : formStatus);

        return validationForm.validated;
    }

    const formSubmit = (e) => {
        e.preventDefault();
        if (handleValidation()) {
            props.throwCaptcha((token) => doRegister({
                token,
                fieldsFormValues: fieldsFormRef.current,
                regulatedCompanyValue: regulatedCompanyRef.current,
                registerCountryValue: registerCountryRef.current,
                referredActiveValue: referredActiveRef.current,
            }));
        }
    }

    const handleChange = (e, ref = null) => {
        const newFieldsForm = {...fieldsForm};
        if (e.target.type === 'checkbox') {
            newFieldsForm[e.target.name] = typeof newFieldsForm[e.target.name] === 'undefined' ? true : !newFieldsForm[e.target.name];
        } else {
            newFieldsForm[e.target.name] = e.target.value;
        }
        setFieldsForm(newFieldsForm);
        setFormStatus(false);
        setErrorsRequest(null);

        if (timeoutForm) {
            clearTimeout(timeoutForm);
        }
        const newTimeoutForm = setTimeout(() => handleValidation(ref), 500);
        setTimeoutForm(newTimeoutForm);
    }

    const statusUsername = checkStatusField(fieldsStatus['username']);
    const statusEmail = checkStatusField(fieldsStatus['email']);
    const statusPassword = checkStatusField(fieldsStatus['password']);
    const statusCommercialCommunicationsAccepted = checkStatusField(fieldsStatus['commercialCommunicationsAccepted']);

    const disabledRegulatedCompany = regulatedCompany === '' || regulatedCompany === regulatedCompaniesEnum.DX_ZERO;

    return (
        <AuthLayout
            signUp
        >
            <SignUpInfo showZeroAlert={regulatedCompany!==regulatedCompaniesEnum.DX_SYC}/>
            <SignUpForm
                affiliate={affiliateName}
                referredActive={referredActive}
                errors={errorsRequest}
                formSubmit={e => formSubmit(e)}
                disabledSignUpButton={formStatus === null || disabledRegulatedCompany}
                disabledRegulatedCompany={disabledRegulatedCompany}
                handleChange={(e, ref) => handleChange(e, ref)}
                handleChangeRegisterCountry={(e) => updateCountryAndRegulatedCompany(e)}
                handleChangeAffiliate={(e) => setReferredActive(e.target.checked)}
                handleChangeRegulatedCompany={(e) => setRegulatedCompany(e.target.value)}
                usernameInput={usernameInput}
                usernameValue={fieldsForm["username"] || ''}
                usernameSuccess={statusUsername.status === 'success'}
                usernameError={statusUsername.status === 'error' && typeof fieldsStatusType['username'] !== 'undefined' && fieldsStatusType['username'].type === 'error'}
                usernameErrorMsg={statusUsername.error}
                usernameWarning={statusUsername.status === 'error' && typeof fieldsStatusType['username'] !== 'undefined' && fieldsStatusType['username'].type === 'warning'}
                emailInput={emailInput}
                emailValue={fieldsForm["email"] || ''}
                emailSuccess={statusEmail.status === 'success'}
                emailError={statusEmail.status === 'error' && typeof fieldsStatusType['email'] !== 'undefined' && fieldsStatusType['email'].type === 'error'}
                emailErrorMsg={statusEmail.error}
                emailWarning={statusEmail.status === 'error' && typeof fieldsStatusType['email'] !== 'undefined' && fieldsStatusType['email'].type === 'warning'}
                passwordInput={passwordInput}
                passwordValue={fieldsForm["password"] || ''}
                passwordSuccess={statusPassword.status === 'success' && typeof fieldsStatusType['password'] !== 'undefined' && fieldsStatusType['password'].type === 'success'}
                passwordError={statusPassword.status === 'error' && typeof fieldsStatusType['password'] !== 'undefined' && fieldsStatusType['password'].type === 'error'}
                passwordErrorMsg={statusPassword.status === 'error' ? statusPassword.error : (typeof fieldsStatusType['password'] !== 'undefined' ? fieldsStatusType['password'].message : null)}
                passwordWarning={statusPassword.status === 'success' && typeof fieldsStatusType['password'] !== 'undefined' && fieldsStatusType['password'].type === 'warning'}
                commercialCommunicationsAcceptedInput={commercialCommunicationsAcceptedInput}
                commercialCommunicationsAcceptedChecked={fieldsForm["commercialCommunicationsAccepted"] || false}
                commercialCommunicationsAcceptedSuccess={statusCommercialCommunicationsAccepted.status === 'success'}
                commercialCommunicationsAcceptedError={statusCommercialCommunicationsAccepted.status === 'error'}
                commercialCommunicationsAcceptedErrorMsg={statusCommercialCommunicationsAccepted.error}
                regulatedCompany={regulatedCompany}
                regulatedCompanies={regulatedCompanies}
                registerCountry={registerCountry}
                registerCountries={registerCountries}
                showModalCompareRegulatedCompanies={(e) => {
                    e.preventDefault();
                    showModal('compareRegulatedCompanies');
                }}
            />
            {modals && modals.current === 'darwinexNotAvailableInYourCountry' && <NotAvailableInYourCountryModal onClose={()=>hideModal()}/>}
            {modals && modals.current === 'warningDxSyc' && <WarningDxSyc
                onClose={()=> {
                    setRegulatedCompany(regulatedCompaniesEnum.DX_CNMV);
                    hideModal();
                }}
                onContinue={()=> hideModal()}
            />}
            {modals && modals.current === 'warningDxFca' && <WarningDxFca
                onClose={()=> {
                    setRegulatedCompany(regulatedCompaniesEnum.DX_CNMV);
                    hideModal();
                }}
                onContinue={()=> hideModal()}
            />}
            {modals && modals.current === 'compareRegulatedCompanies' && <CompareRegulatedCompanies
                onClose={()=> hideModal()}
                regulatedCompanies={regulatedCompanies}
                regulatedCompany={regulatedCompany}
                setRegulatedCompany={(regCo) => {
                    setRegulatedCompany(regCo);
                }}
            />}
        </AuthLayout>
    );
}

export default withRouter(withProfile(withCopies(withCaptcha(RegisterPage))));
